import { Modal } from '@/components/ui/modal'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { useState } from 'react'
import { ChevronDown, Users } from 'lucide-react'

export interface ProjectTimeContributor {
  user_id: string
  user_nom: string
  user_email: string | null
  total_hours: number
  total_days: number
  entries: Array<{ work_date: string; duration_hours: number }>
}

export interface ProjectTicketTimeLine {
  ticket_id: number
  ticket_titre: string
  role: 'developpeur' | 'chef_projet' | string
  person_nom: string
  user_id?: string | null
  user_email?: string | null
  days: number
  unit: 'days' | 'hours' | string | null
}

interface PersonTimeCard {
  key: string
  nom: string
  email: string | null
  standupHours: number
  standupDays: number
  entries: ProjectTimeContributor['entries']
  tickets: ProjectTicketTimeLine[]
}

export interface ProjectTimeContributorsData {
  total_hours: number
  total_days: number
  contributors_count: number
  contributors: ProjectTimeContributor[]
  ticket_times?: ProjectTicketTimeLine[]
  ticket_days_total?: number
}

function fmtHours(h: number): string {
  return `${Number(h).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`
}

function fmtDays(d: number): string {
  return `${Number(d).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} j`
}

function fmtTicketTime(days: number, unit: string | null): string {
  if (unit === 'hours') {
    const hours = Math.round(days * 8 * 100) / 100
    return `${Number(hours).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`
  }
  return fmtDays(days)
}

function roleLabel(role: string): string {
  return role === 'chef_projet' ? 'Chef de projet' : 'Développeur'
}

function ticketTotal(person: PersonTimeCard): string | null {
  if (person.tickets.length === 0) return null
  const days = person.tickets.reduce((sum, line) => sum + line.days, 0)
  const allHours = person.tickets.every((line) => line.unit === 'hours')
  if (allHours) return fmtHours(Math.round(days * 8 * 100) / 100)
  return fmtDays(days)
}

function buildPeople(data: ProjectTimeContributorsData): PersonTimeCard[] {
  const people = new Map<string, PersonTimeCard>()
  for (const contributor of data.contributors) {
    people.set(contributor.user_id, {
      key: contributor.user_id,
      nom: contributor.user_nom,
      email: contributor.user_email,
      standupHours: contributor.total_hours,
      standupDays: contributor.total_days,
      entries: contributor.entries,
      tickets: [],
    })
  }
  for (const line of data.ticket_times ?? []) {
    const byId = line.user_id ? people.get(line.user_id) : undefined
    const byName = [...people.values()].find((person) => person.nom === line.person_nom)
    const person = byId ?? byName
    if (person) {
      person.tickets.push(line)
      continue
    }
    const key = line.user_id || `ticket:${line.person_nom}`
    const created = people.get(key) ?? {
      key,
      nom: line.person_nom || '—',
      email: line.user_email ?? null,
      standupHours: 0,
      standupDays: 0,
      entries: [],
      tickets: [],
    }
    created.tickets.push(line)
    people.set(key, created)
  }
  return [...people.values()].sort((a, b) => {
    if (b.standupHours !== a.standupHours) return b.standupHours - a.standupHours
    return a.nom.localeCompare(b.nom, 'fr')
  })
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

export interface ProjectTimeContributorsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectName: string
  data: ProjectTimeContributorsData | null
  loading?: boolean
}

export function ProjectTimeContributorsModal({
  open,
  onOpenChange,
  projectName,
  data,
  loading = false,
}: ProjectTimeContributorsModalProps) {
  const people = data ? buildPeople(data) : []
  const ticketDays = data?.ticket_days_total ?? 0
  const [openStandup, setOpenStandup] = useState<Set<string>>(new Set())
  const toggleStandup = (key: string) =>
    setOpenStandup((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-2 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Temps passé sur le projet</h2>
            <p className="text-sm text-gray-500 mt-1">{projectName}</p>
          </div>
        </div>

        {loading ? (
          <PageSkeleton variant="rows" />
        ) : people.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            Aucune saisie de temps enregistrée pour ce projet.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-1 text-sm text-gray-500">
              <span>
                <strong className="font-semibold text-gray-900">{people.length}</strong>{' '}
                {people.length > 1 ? 'personnes' : 'personne'}
              </span>
              {data && data.total_hours > 0 ? (
                <span>
                  <strong className="font-semibold text-gray-900">{fmtHours(data.total_hours)}</strong>
                  <span className="text-gray-400"> · ≈ {fmtDays(data.total_days)}</span>
                </span>
              ) : null}
              {ticketDays > 0 ? (
                <span>
                  <strong className="font-semibold text-gray-900">{fmtDays(ticketDays)}</strong> sur les tickets
                </span>
              ) : null}
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar pr-1">
              {people.map((person) => (
                <div
                  key={person.key}
                  className="rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-violet-50">
                    <div>
                      <div className="font-semibold text-gray-900">{person.nom}</div>
                      {person.email && (
                        <div className="text-xs text-gray-500">{person.email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-5 text-right">
                      {person.standupHours > 0 && (
                        <div>
                          <div className="font-bold text-violet-700">{fmtHours(person.standupHours)}</div>
                          <div className="text-xs text-gray-500">Standup</div>
                        </div>
                      )}
                      {ticketTotal(person) && (
                        <div>
                          <div className="font-bold text-violet-700">{ticketTotal(person)}</div>
                          <div className="text-xs text-gray-500">Tickets</div>
                        </div>
                      )}
                    </div>
                  </div>
                  {person.tickets.map((line, index) => (
                    <div
                      key={`${line.ticket_id}-${line.role}-${index}`}
                      className="flex items-center justify-between gap-4 border-t border-gray-100 px-4 py-2.5"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm text-gray-900">{line.ticket_titre}</div>
                        <div className="text-xs text-gray-400">{roleLabel(line.role)}</div>
                      </div>
                      <div className="shrink-0 text-sm font-medium text-gray-900">
                        {fmtTicketTime(line.days, line.unit)}
                      </div>
                    </div>
                  ))}
                  {person.entries.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleStandup(person.key)}
                      className="flex w-full items-center justify-between border-t border-gray-100 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <span>
                        Saisies standup ({person.entries.length})
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform ${openStandup.has(person.key) ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                  {person.entries.length > 0 && openStandup.has(person.key) && (
                    <table className="w-full text-sm border-t border-gray-100">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100">
                          <th className="py-2 px-4 font-medium">Date</th>
                          <th className="py-2 px-4 font-medium text-right">Durée</th>
                        </tr>
                      </thead>
                      <tbody>
                        {person.entries.map((entry, idx) => (
                          <tr key={`${entry.work_date}-${idx}`} className="border-b border-gray-50 last:border-0">
                            <td className="py-2 px-4 text-gray-800">{fmtDate(entry.work_date)}</td>
                            <td className="py-2 px-4 text-right text-gray-800 font-medium">
                              {fmtHours(entry.duration_hours)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
